'use client'

import { Theme, useTheme } from '@mui/material'
import Typography from '@mui/material/Typography'
import { ReactElement } from 'react'
import { tss } from 'tss-react/mui'

const useStyles = tss.create(({ theme }) => ({
    categoryPillRoot: {
        position: 'relative',
        fontWeight: 600,
        border: `${theme.spacing(0.5)} solid ${theme.palette.common.white}`,
        borderRadius: '20px',
        fontSize: 12,
        color: theme.palette.common.white,
    },
    withCommunity: {
        textTransform: 'none',
        marginTop: theme.spacing(-5.5),
        marginBottom: theme.spacing(2.5),
        marginLeft: theme.spacing(1.5),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.75),
    },
    withoutCommunity: {
        textTransform: 'uppercase',
        marginTop: theme.spacing(-2),
        marginLeft: theme.spacing(1.5),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    communityPill: {
        position: 'relative',
        backgroundColor: theme.palette.common.white,
        color: theme.palette.text.secondary,
        border: `${theme.spacing(0.5)} solid ${theme.palette.common.white}`,
        borderRadius: '20px',
        textTransform: 'uppercase',
        marginTop: theme.spacing(-1.5),
        marginLeft: theme.spacing(1.5),
        marginRight: theme.spacing(1.5),
        paddingTop: theme.spacing(0.25),
        paddingBottom: theme.spacing(0.25),
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
    },
}))

type CategoryData = { text: string; bgcolor: (theme: Theme) => string }

const categoryPillMap: { [key: string]: CategoryData } = {
    'Official Event': { text: 'Official Event', bgcolor: (theme) => theme.palette.warning.main },
    'Newcomers Event': { text: 'Newcomers', bgcolor: (theme) => theme.palette.error.main },
} as const

type CategoryPillProps = { category?: string; showCommunity?: boolean }

export function CategoryPill({ category, showCommunity }: CategoryPillProps): ReactElement | null {
    const { classes, cx } = useStyles()
    const categoryData =
        category && Object.prototype.hasOwnProperty.call(categoryPillMap, category) && category in categoryPillMap
            ? categoryPillMap[category]
            : null
    const theme = useTheme()
    const style = categoryData ? { backgroundColor: category ? categoryData.bgcolor(theme) : undefined } : undefined

    if (!categoryData || !category) {
        return null
    }

    const categoryPill = categoryPillMap[category]

    if (!categoryPill) {
        return null
    }

    return (
        <Typography
            variant="overline"
            style={style}
            className={cx(classes.categoryPillRoot, showCommunity ? classes.withCommunity : classes.withoutCommunity)}
        >
            {categoryData.text}
        </Typography>
    )
}

export function CommunityPill({ community }: { community?: string }): ReactElement | null {
    const { classes, cx } = useStyles()

    if (!community) {
        return null
    }

    return (
        <Typography variant="overline" className={cx('line-clamp-1', classes.communityPill)}>
            {community}
        </Typography>
    )
}
